/**
 * Facility scope for a single booking visit.
 *
 * "Scope" = which hospital this visit is locked to, set by an entry link (QR today,
 * /hospital/:slug in Phase 2). It lives in sessionStorage so it is per-tab/visit and
 * a stale selection from a previous visit never leaks into a later cold open — a cold
 * visit has no scope, so the app shows the full hospital list (the default).
 *
 * A manual pick from /hospitals is NOT a scope: those users want the list and can
 * return to it. Only entry-link scopes cause /hospitals to be skipped.
 */

export type FacilityEntrySource = "qr" | "facility-web" | "manual";

export interface FacilityScope {
  facilityId: number;
  source: FacilityEntrySource;
  at: number;
}

const SCOPE_KEY = "pc_facilityScope";

const isValidId = (n: unknown): n is number =>
  typeof n === "number" && Number.isFinite(n) && n > 0;

/** Lock this visit to a hospital (from an entry link). Mirrors legacy keys for older code. */
export const setFacilityScope = (
  facilityId: number,
  source: FacilityEntrySource
): void => {
  const id = Number(facilityId);
  if (!isValidId(id)) return;
  const scope: FacilityScope = { facilityId: id, source, at: Date.now() };
  try {
    sessionStorage.setItem(SCOPE_KEY, JSON.stringify(scope));
    sessionStorage.setItem("qrFacilityId", String(id)); // legacy readers
  } catch {
    /* storage may be unavailable in some embedded contexts */
  }
  try {
    localStorage.setItem("selectedFacilityId", String(id));
    localStorage.setItem("hospitalSelected", "true");
  } catch {
    /* ignore */
  }
};

/** The hospital this visit is scoped to, or null. Falls back to the legacy QR key. */
export const getFacilityScope = (): FacilityScope | null => {
  try {
    const raw = sessionStorage.getItem(SCOPE_KEY);
    if (raw) {
      const parsed = JSON.parse(raw) as FacilityScope;
      if (parsed && isValidId(parsed.facilityId)) return parsed;
    }
    const legacy = sessionStorage.getItem("qrFacilityId");
    if (legacy && /^\d+$/.test(legacy)) {
      return { facilityId: parseInt(legacy, 10), source: "qr", at: 0 };
    }
  } catch {
    /* ignore */
  }
  return null;
};

export const hasActiveFacilityScope = (): boolean => getFacilityScope() !== null;

// True while on the facility-entry route (/hospital or /hospital/:slug), i.e. a
// scope is about to be set. Used to avoid fetching the full facility list in that
// transient window before the scope resolves.
export const isOnFacilityEntry = (): boolean => {
  try {
    const p = window.location.pathname;
    return p === "/hospital" || p.startsWith("/hospital/");
  } catch {
    return false;
  }
};

/** Drop the visit scope and any mirrored persisted selection. */
export const clearFacilityScope = (): void => {
  try {
    sessionStorage.removeItem(SCOPE_KEY);
    sessionStorage.removeItem("qrFacilityId");
  } catch {
    /* ignore */
  }
  try {
    localStorage.removeItem("selectedFacilityId");
    localStorage.removeItem("hospitalSelected");
    localStorage.removeItem("fromQR");
  } catch {
    /* ignore */
  }
};
